import { Flex, LoadingSpin, PasswordRequirementsGuide } from '@/components';
import {
  gererateCaptcha,
  resetPassword,
  resetPasswordInit,
  resetPasswordVerifyOtp,
} from '@/services/user.service';
import uuidv4 from '@/utils/uuidGenerator';
import parseError from '@/utils/parseError';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Input } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReloadOutlined } from '@ant-design/icons';
import {
  AuthorizeUserInitValues,
  RestPasswordInitValues,
  useFormAuthorizeUser,
  useFormRestPassword,
  useFormVerifyOtp,
  VerifyOtpInitValues,
} from '@/hooks/app/useFormRestPassword';
import usePasswordConfig from '@/hooks/app/usePasswordConfig';
import type { PasswordValidationResult } from '@/types/passwordConfig';

enum RestPassSteps {
  EnterUsername,
  VerifyOTP,
  ResetPassword,
}

export function ResetPassword({ changePage }: { changePage: (isLogin: boolean) => void }) {
  const [t] = useTranslation(['common']);
  const { message } = App.useApp();

  const [step, setStep] = useState(RestPassSteps.EnterUsername);
  const [resetUsername, setResetUsername] = useState('');
  const [nonce, setNonce] = useState(uuidv4());
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);

  const messageOpts = { duration: 2, style: { marginTop: '2rem' } };

  const { mutate: getCaptcha, isPending: isCaptchaPending, data: captchaPayload } = useMutation({
    mutationKey: ['/rest/captcha', nonce],
    mutationFn: async () => gererateCaptcha({ nonce }),
  });

  useEffect(() => {
    getCaptcha();
  }, [nonce]);

  const refreshCaptcha = useCallback(() => {
    setNonce(uuidv4());
  }, []);

  const { mutate: submitInit, isPending: isInitPending } = useMutation({
    mutationKey: ['/rest/users/reset-password/init'],
    mutationFn: async (values: AuthorizeUserInitValues) => {
      const captchaValidation = captchaPayload?.data?.payload?.validation;
      if (!captchaValidation) {
        throw new Error(t('common:errors.captcha-not-ready'));
      }
      return resetPasswordInit({
        username: values.username,
        captchaEnteredByUser: values.captcha,
        captchaValidation,
        nonce,
      });
    },
    onSuccess: (_data, values) => {
      setResetUsername(values.username);
      setStep(RestPassSteps.VerifyOTP);
      message.success({ ...messageOpts, content: t('common:messages.reset-password-otp-sent') });
    },
  });

  const { mutate: submitOtp, isPending: isOtpPending } = useMutation({
    mutationKey: ['/rest/users/reset-password/verify-otp'],
    mutationFn: async (values: VerifyOtpInitValues) => resetPasswordVerifyOtp({ otp: values.otp }),
    onSuccess: () => {
      setStep(RestPassSteps.ResetPassword);
      message.success({ ...messageOpts, content: t('common:messages.reset-password-otp-verified') });
    },
    onError: (error: unknown) => {
      message.error({ ...messageOpts, content: parseError(error, t('common:errors.auth-connect')) });
    },
  });

  const { mutate: submitNewPassword, isPending: isResetPending } = useMutation({
    mutationKey: ['/rest/users/reset-password'],
    mutationFn: async (values: RestPasswordInitValues) =>
      resetPassword({
        newPassword: values.newPassword,
        confirmNewPassword: values.confirmNewPassword,
      }),
    onSuccess: () => {
      message.success({ ...messageOpts, content: t('common:messages.success-password-change') });
      changePage(true);
    },
    onError: (error: unknown) => {
      message.error({ ...messageOpts, content: parseError(error, t('common:errors.auth-connect')) });
    },
  });

  const { config, requirements, validatePassword } = usePasswordConfig(resetUsername);

  const onPasswordSubmit = useCallback((values: RestPasswordInitValues) => {
    const validation = validatePassword(values.newPassword || '');
    setPasswordValidation(validation);
    if (!validation.isValid) {
      return;
    }
    submitNewPassword(values);
  }, [submitNewPassword, validatePassword]);

  const onAuthorizeSubmit = useCallback((values: AuthorizeUserInitValues, helpers: { setFieldValue: (field: string, value: string) => void }) => {
    submitInit(values, {
      onError: (error: unknown) => {
        message.error({ ...messageOpts, content: parseError(error, t('common:errors.auth-connect')) });
        helpers.setFieldValue('captcha', '');
        refreshCaptcha();
      },
    });
  }, [submitInit, message, t, refreshCaptcha]);

  const onOtpSubmit = useCallback((values: VerifyOtpInitValues) => {
    submitOtp(values);
  }, [submitOtp]);

  const authorizeForm = useFormAuthorizeUser(onAuthorizeSubmit);
  const otpForm = useFormVerifyOtp(onOtpSubmit);
  const passwordForm = useFormRestPassword(onPasswordSubmit);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    passwordForm.handleChange(e);
    setPasswordValidation(validatePassword(e.target.value));
  }, [passwordForm, validatePassword]);

  const showPasswordErrors =
    passwordForm.values.newPassword.length > 0 &&
    passwordValidation?.errors &&
    passwordValidation.errors.length > 0;

  return (
    <Flex direction="column" gap="1rem">
      <Flex
        align="center"
        justify="center"
        direction="column"
        className="sidebar-title">
        {t('common:messages.reset-password-title')}
      </Flex>

      {step === RestPassSteps.EnterUsername && (
        <Flex direction="column">
          <Input
            name="username"
            spellCheck="false"
            className="auth-input"
            value={authorizeForm.values.username}
            onChange={authorizeForm.handleChange}
            placeholder={t('common:labels.username')}
            onKeyDown={e => e.key === 'Enter' ? authorizeForm.submitForm() : undefined}
          />
          <Flex>
            <Input
              name="captcha"
              spellCheck="false"
              className="auth-input w-1/2"
              value={authorizeForm.values.captcha}
              onChange={authorizeForm.handleChange}
              placeholder={t('common:labels.captcha')}
              onKeyDown={e => e.key === 'Enter' ? authorizeForm.submitForm() : undefined}
            />
            <Flex className="w-1/2">
              {isCaptchaPending ? (
                <LoadingSpin className="text-primary" text="" />
              ) : (
                <img
                  src={`data:image/png;base64,${captchaPayload?.data?.payload?.image || ''}`}
                  alt="captcha"
                  className="w-[110px]"
                />
              )}
              <Button onClick={refreshCaptcha}><ReloadOutlined /></Button>
            </Flex>
          </Flex>
          <Button
            className="auth-btn mb-0"
            loading={isInitPending}
            disabled={isCaptchaPending || !captchaPayload || !authorizeForm.isValid}
            onClick={authorizeForm.submitForm}
          >
            {t('common:labels.authentication')}
          </Button>
        </Flex>
      )}

      {step === RestPassSteps.VerifyOTP && (
        <Flex direction="column" gap="0.5rem">
          <p className="text-sm text-center mb-2">{t('common:messages.reset-password-otp-hint')}</p>
          <Input
            name="otp"
            spellCheck="false"
            className="auth-input"
            value={otpForm.values.otp}
            onChange={otpForm.handleChange}
            placeholder={t('common:labels.otp-code')}
            maxLength={8}
            inputMode="numeric"
            onKeyDown={e => e.key === 'Enter' ? otpForm.submitForm() : undefined}
          />
          <Button
            className="auth-btn mb-0"
            loading={isOtpPending}
            disabled={!otpForm.isValid}
            onClick={otpForm.submitForm}
          >
            {t('common:labels.verify-otp')}
          </Button>
          <Flex
            inlineFlex
            component="b"
            className="justify-center text-center text-xs cursor-pointer text-primary-dark"
            onClick={() => setStep(RestPassSteps.EnterUsername)}
          >
            {t('common:labels.back')}
          </Flex>
        </Flex>
      )}

      {step === RestPassSteps.ResetPassword && (
        <Flex direction="column" gap="0.5rem">
          <Input.Password
            name="newPassword"
            spellCheck="false"
            className="auth-input"
            value={passwordForm.values.newPassword}
            onChange={handlePasswordChange}
            placeholder={t('common:labels.new-password')}
            onKeyDown={e => e.key === 'Enter' ? passwordForm.submitForm() : undefined}
          />
          {showPasswordErrors && (
            <div className="password-errors-list">
              {passwordValidation?.errors.map((error, index) => (
                <div key={index} className="text-red-500 text-xs">
                  {error}
                </div>
              ))}
            </div>
          )}
          <PasswordRequirementsGuide config={config} requirements={requirements} />
          <Input.Password
            name="confirmNewPassword"
            spellCheck="false"
            className="auth-input"
            value={passwordForm.values.confirmNewPassword}
            onChange={passwordForm.handleChange}
            placeholder={t('common:labels.confirm-new-password')}
            onKeyDown={e => e.key === 'Enter' ? passwordForm.submitForm() : undefined}
          />
          <Button
            className="auth-btn mb-0"
            loading={isResetPending}
            disabled={!passwordForm.isValid}
            onClick={passwordForm.submitForm}
          >
            {t('common:labels.set-new-password')}
          </Button>
        </Flex>
      )}

      <Flex
        inlineFlex
        component="b"
        className="justify-center text-center text-xs cursor-pointer text-primary-dark"
        onClick={() => changePage(true)}
      >
        {t('common:labels.login')}
      </Flex>
    </Flex>
  );
}

export default ResetPassword;
